import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  FormHelperText,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputAdornment,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import countryTelephoneData from "country-telephone-data";

import ISelect from "./SelectField";
import ITimePicker from "./ITimePicker";
import indianCities from "./indianCities";
import { useCreateInterestedClientMutation } from "services/clientApi";
import { useLoading } from "./LoadingProvider";
import { useSnackbar } from "./SnackbarProvider";
import { ClientStatuses } from "CommonData/Common";

const ClientInterestForm = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { startLoading, stopLoading } = useLoading();
  const [createInterestedClient] = useCreateInterestedClientMutation();

  const countryList = Array.from(
    new Set(
      countryTelephoneData.allCountries.map((c) => c.name.split("(")[0].trim()),
    ),
  );

  const getPhoneCode = (countryName) => {
    const country = countryTelephoneData.allCountries.find(
      (c) => c.name.split("(")[0].trim() === countryName,
    );
    return country ? country.dialCode : "";
  };

  const initialFormValue = {
    firstName: "",
    lastName: "",
    email: "",
    countryType: "",
    contactNumber: "",
    serviceLocation: "",
    serviceType: "",
    dayofchoice: "Anyday",
    availableFrom: null,
    availableTo: null,
    initialComment: "",
  };

  const [formValues, setFormValues] = useState(initialFormValue);
  const [formErrors, setFormErrors] = useState({});

  // FORCE RESET ON EVERY LOAD
  useEffect(() => {
    setFormValues(initialFormValue);
    setFormErrors({});
  }, []);

  // Function to validate and format name fields (allows only letters, spaces, and common name characters)
  const formatName = (value) => {
    // Allow letters, spaces, hyphens, apostrophes (common in names)
    // Adjust the regex if you need different rules
    return value.replace(/[^A-Za-z\s\-']/g, "");

    // If you want to allow only letters and spaces (no hyphens or apostrophes):
    // return value.replace(/[^A-Za-z\s]/g, '');

    // If you want to allow only letters (no spaces):
    // return value.replace(/[^A-Za-z]/g, '');
  };

  const handleInputChange = (name, value) => {
    let processedValue = value;

    // Apply name formatting only to firstName and lastName fields
    if (name === "firstName" || name === "lastName") {
      processedValue = formatName(value);
    }

    setFormValues((prev) => ({ ...prev, [name]: processedValue }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleContactChange = (value) => {
    const numeric = value.replace(/[^0-9]/g, "");
    if (numeric.length <= 10) {
      handleInputChange("contactNumber", numeric);
    }
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Additional validation for names
  const validateName = (name, fieldName) => {
    if (!name.trim()) {
      return `${fieldName} is required`;
    }

    // Check if name contains only valid characters
    if (!/^[A-Za-z\s\-']+$/.test(name)) {
      return `${fieldName} should contain only letters, spaces, hyphens, and apostrophes`;
    }

    // Optional: Minimum length check
    if (name.trim().length < 2) {
      return `${fieldName} should be at least 2 characters`;
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {
      firstName: validateName(formValues.firstName, "First name"),
      lastName: validateName(formValues.lastName, "Last name"),
      email: !validateEmail(formValues.email) ? "Valid email required" : "",
      countryType: !formValues.countryType ? "Country required" : "",
      contactNumber: !formValues.contactNumber ? "Contact number required" : "",
    };

    setFormErrors(errors);

    if (!Object.values(errors).every((e) => e === "")) return;

    try {
      startLoading();

      const payload = {
        firstName: formValues.firstName.trim(),
        lastName: formValues.lastName.trim(),
        email: formValues.email,
        country: formValues.countryType,
        contactNumber:
          "+" +
          getPhoneCode(formValues.countryType) +
          "-" +
          formValues.contactNumber,
        serviceLocation: formValues.serviceLocation,
        serviceType: formValues.serviceType,
        dayofchoice: formValues.dayofchoice,
        availabilityStartTime: formValues.availableFrom?.format("HH:mm"),
        availabilityEndTime: formValues.availableTo?.format("HH:mm"),
        initialComment: formValues.initialComment,
        status: ClientStatuses.INTERESTED.name,
      };

      await createInterestedClient(payload).unwrap();
      showSnackbar("Appointment booked successfully", "success");
      navigate("/");
    } catch (err) {
      showSnackbar("Something went wrong. Please try again.", "error");
    } finally {
      stopLoading();
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={4} sx={{ p: 4, borderRadius: 4, mt: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={600}>
            Book Trusted Home Care
          </Typography>
          <IconButton onClick={() => navigate("/")}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box mt={3}>
          <form
            autoComplete="off"
            name={`client-form-${Date.now()}`}
            onSubmit={handleSubmit}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  name="first_name_random"
                  fullWidth
                  value={formValues.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  autoComplete="off"
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                  inputProps={{
                    maxLength: 50, // Optional: Limit the length
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  name="last_name_random"
                  fullWidth
                  value={formValues.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  autoComplete="off"
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                  inputProps={{
                    maxLength: 50, // Optional: Limit the length
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email_random"
                  fullWidth
                  value={formValues.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  autoComplete="new-password"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ISelect
                  label="Country"
                  value={formValues.countryType}
                  options={countryList}
                  onChange={(e, value) =>
                    handleInputChange("countryType", value)
                  }
                />
                <FormHelperText error>{formErrors.countryType}</FormHelperText>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contact Number"
                  name="phone_random"
                  fullWidth
                  value={formValues.contactNumber}
                  onChange={(e) => handleContactChange(e.target.value)}
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {formValues.countryType &&
                          "+" + getPhoneCode(formValues.countryType)}
                      </InputAdornment>
                    ),
                  }}
                  error={!!formErrors.contactNumber}
                  helperText={formErrors.contactNumber}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ISelect
                  label="Service Location"
                  options={indianCities}
                  value={formValues.serviceLocation}
                  onChange={(e, value) =>
                    handleInputChange("serviceLocation", value)
                  }
                />
              </Grid>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid item xs={12} sm={6}>
                  <ITimePicker
                    label="Available From"
                    value={formValues.availableFrom}
                    onChange={(v) => handleInputChange("availableFrom", v)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <ITimePicker
                    label="Available To"
                    value={formValues.availableTo}
                    minTime={
                      formValues.availableFrom
                        ? dayjs(formValues.availableFrom).add(30, "minute")
                        : null
                    }
                    onChange={(v) => handleInputChange("availableTo", v)}
                  />
                </Grid>
              </LocalizationProvider>

              <Grid item xs={12}>
                <TextField
                  label="Initial Comments"
                  fullWidth
                  multiline
                  rows={2}
                  value={formValues.initialComment}
                  onChange={(e) =>
                    handleInputChange("initialComment", e.target.value)
                  }
                  autoComplete="off"
                />
              </Grid>

              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="flex-end"
                gap={2}
              >
                <Button variant="outlined" onClick={() => navigate("/")}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained">
                  Book Appointment
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
    </Container>
  );
};

export default ClientInterestForm;
